import { useEffect, useRef, useState } from 'react'
import { App as CapacitorApp } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { StatusBar } from '@capacitor/status-bar'
// import { SplashScreen } from '@capacitor/splash-screen'
import { Network } from '@capacitor/network'
import { useLocation } from 'react-router-dom'
import { isVersionGreaterOrEqual } from 'Utility/globalMethods'
import { FirebaseMessaging } from 'merchant-product-push-plugin'
import componentConfig from 'Utility/globalConfig'
import initSecurity from 'Utility/appSecurity/initSecurity'
import { getSalesforceInAppEventsForPath } from 'Utility/salesforceInAppEvents'

const AppInit = ({
  onNetworkDisconnected,
  getAppVersions,
  minVersion,
  showPopUpShouldUpdateApp,
  logged,
  sendAppId,
  contactKey,
  showPopUpRooted,
  showPopUpSslInvalid
}) => {
  const location = useLocation()
  const [isAppActive, setIsAppActive] = useState(false)
  const [statusBarReady, setStatusBarReady] = useState(false)
  const isRestoringStatusBar = useRef(false)
  const [MCinitialized, setMCinitialized] = useState(false)
  const [isContactKeySet, setIsContactKeySet] = useState(false)
  const lastTrackedInAppRouteRef = useRef(null)
  const marketingCloudInitializationRef = useRef(null)

  const showStatusBarMask = () => {
    if (document.getElementById('sb-restore-mask')) return
    const mask = document.createElement('div')
    mask.id = 'sb-restore-mask'
    // Cover the native status-bar area with the app background colour so the
    // user never sees the bar jump during restoration.
    mask.style.cssText =
      'position:fixed;top:0;left:0;right:0;' +
      'height:env(safe-area-inset-top,44px);' +
      'background:#ffffff;z-index:2147483647;pointer-events:none;' +
      'transition:opacity 0.15s ease;'
    document.body.appendChild(mask)
  }

  const hideStatusBarMask = () => {
    const mask = document.getElementById('sb-restore-mask')
    if (!mask) return
    mask.style.opacity = '0'
    setTimeout(() => mask.remove(), 160)
  }

  const restoreStatusBar = async () => {
    if (isRestoringStatusBar.current) return
    isRestoringStatusBar.current = true
    showStatusBarMask()
    try {
      // Force a toggle true→false so the native plugin always recalculates the
      // WebView frame.  Without this, setOverlaysWebView(false) is a no-op when
      // the internal state is already false – but the actual frame may have been
      // disrupted by returning from an external app (e.g. PDF viewer on iOS).
      await StatusBar.setOverlaysWebView({ overlay: true })
      await StatusBar.setOverlaysWebView({ overlay: false })
      await StatusBar.setBackgroundColor({ color: '#ffffff' })
      await StatusBar.setStyle({ style: 'LIGHT' })
      await StatusBar.show()
    } catch (e) {
      console.error('[StatusBar] Error restoring', e, { userAgent: navigator.userAgent })
    } finally {
      // Give the native side ~200 ms to paint before revealing the area.
      setTimeout(hideStatusBarMask, 200)
      isRestoringStatusBar.current = false
    }
  }

  // Expose a safe global helper so other modules (e.g. FileOpener helpers)
  // can request a status bar restore after returning from external apps.
  if (typeof window !== 'undefined') {
    window.restoreStatusBarSafe = () => {
      // Delay slightly to let the webview regain focus, then restore once.
      setTimeout(() => {
        restoreStatusBar().catch((err) => console.error('[StatusBar] restoreStatusBarSafe failed', err))
      }, 150)
    }
  }

  // Gestione robusta della status bar dopo resume/appStateChange (es. post apertura PDF)
  useEffect(() => {
    const handleAppStateChange = async ({ isActive }) => {
      if (isActive) {
        try {
          await restoreStatusBar()
        } catch (e) {
          console.error('[StatusBar] error during restore on appStateChange', e)
        }
        setIsAppActive(true)
      }
    }

    const sub = CapacitorApp.addListener('appStateChange', handleAppStateChange)

    return () => {
      if (sub && typeof sub.remove === 'function') sub.remove()
    }
  }, [])

  // Additional listeners: some native file openers / external activities don't
  // always trigger `appStateChange`. Listen to resume/focus/visibility to
  // ensure the status bar is restored when the webview regains control.
  useEffect(() => {
    let resumeSub = null
    const onFocus = () => {
      restoreStatusBar().catch((e) => console.error('[StatusBar] restore on focus failed', e))
      setIsAppActive(true)
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        restoreStatusBar().catch((e) => console.error('[StatusBar] restore on visibility change failed', e))
        setIsAppActive(true)
      }
    };

    (async () => {
      try {
        resumeSub = await CapacitorApp.addListener('resume', () => {
          restoreStatusBar().catch((e) => console.error('[StatusBar] restore on resume failed', e))
          setIsAppActive(true)
        })
      } catch (e) {
        // ignore if addListener not available
      }
    })()

    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      if (resumeSub && typeof resumeSub.remove === 'function') resumeSub.remove()
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  useEffect(() => {
    setIsAppActive(true)
    Network.addListener('networkStatusChange', (status) => {
      checkConnection()
    })
    return () => {
      // Non rimuovere tutti i listener globalmente, solo quelli aggiunti localmente (già gestito sopra)
    }
  }, [])

  useEffect(() => {
    if (isAppActive) {
      restoreStatusBar()
        .then(() => setStatusBarReady(true))
        .catch((e) => console.error('[StatusBar] initial config failed', e))
      // secuirityChecks
      initSecurity({
        showPopUpSslInvalid: () => showPopUpSslInvalid(),
        showPopUpRooted: () => showPopUpRooted()
      })
    }
  }, [isAppActive])

  useEffect(() => {
    if (isAppActive && statusBarReady) {
      // setTimeout(() => {
      //   console.log('🎉 Nascondo lo SplashScreen dopo 500ms...')
      //   // SplashScreen.hide()
      // }, 500)

      checkConnection()

      getAppVersions()

      // Config Push Notification
      if (logged && !window.location.href.includes('login')) {
        configPushNotification()
      }
    }
  }, [isAppActive, statusBarReady, logged])

  useEffect(() => {
    if (minVersion && minVersion !== '') {
      const myVersion = process.env.VERSION_NUMBER
      const showPopUpShouldUpdateFlag = !isVersionGreaterOrEqual(myVersion, minVersion)
      if (showPopUpShouldUpdateFlag) {
        showPopUpShouldUpdateApp()
      }
    }
  }, [minVersion])

  useEffect(() => {
    let isCurrentContactKey = true

    setIsContactKeySet(false)

    if (contactKey && contactKey !== '' && MCinitialized) {
      FirebaseMessaging.setContactKey({ contactKey })
        .then(() => {
          if (isCurrentContactKey) {
            setIsContactKeySet(true)
          }
        })
        .catch((error) => {
          console.error('Failed to set contactKey SFMC', error)
        })
    }

    return () => {
      isCurrentContactKey = false
    }
  }, [contactKey, MCinitialized])

  useEffect(() => {
    if (
      !Capacitor.isNativePlatform() ||
      !logged ||
      !MCinitialized ||
      !contactKey ||
      !isContactKeySet
    ) {
      return
    }

    const events = getSalesforceInAppEventsForPath(location.pathname)
    const routeKey = location.pathname
    const shouldTrackRoute = lastTrackedInAppRouteRef.current !== routeKey

    if (!shouldTrackRoute || !events.length) {
      return
    }

    const trackInAppScreenView = async () => {
      try {
        for (const event of events) {
          await FirebaseMessaging.trackCustomEvent(event)
        }
        lastTrackedInAppRouteRef.current = routeKey
      } catch (error) {
        console.error('[FirebaseMessaging] Error tracking in-app screen view', error)
      }
    }

    trackInAppScreenView()
  }, [location.pathname, logged, MCinitialized, contactKey, isContactKeySet])

  const configPushNotification = async () => {
    if (componentConfig.availableNotifications().includes('marketing_cloud')) {
      configMarketingCloudSdk()
    }

    try {
      // 1. Richiesta permesso
      const { receive } = await FirebaseMessaging.requestPermissions()

      // 2. Iscrizione al topic
      await FirebaseMessaging.subscribeToTopic({ topic: '/topics/merchant-portal' })

      // 3. Gestione token refresh
      FirebaseMessaging.addListener('tokenRefresh', async (data) => {
        const token = data.token
        if (token) {
          window.localStorage.setItem('APP_INSTANCE_ID', token)
        }
      })

      // 4. Primo fetch del token con polling
      getNewFCM(receive === 'granted')
    } catch (err) {
      console.error('[FirebaseMessaging] Error initializing', err)
    }
  }

  const configMarketingCloudSdk = async () => {
    if (!marketingCloudInitializationRef.current) {
      marketingCloudInitializationRef.current = FirebaseMessaging.initializeSFMC({
        config: {
          appId: process.env.APP_ID_MC,
          accessToken: process.env.ACCESS_TOKEN_MC,
          serverUrl: process.env.SERVER_URL_MC,
          mid: process.env.MID_MC,
          senderId: process.env.SENDER_ID_FIREBASE, // Per Android: 1025465021529 //id mittente firebase
          hideInAppBanners: true,
          analyticsEnabled: true
        }
      })
        .then(() => setMCinitialized(true))
        .catch((error) => {
          marketingCloudInitializationRef.current = null
          console.error('[FirebaseMessaging] Error initializing SFMC', error)
        })
    }

    return marketingCloudInitializationRef.current
  }

  const getNewFCM = (permission) => {
    FirebaseMessaging.getToken()
      .then((data) => {
        const token = data.token
        const storedToken = window.localStorage.getItem('APP_INSTANCE_ID')

        if (token && token !== 'null' && token !== storedToken) {
          window.localStorage.setItem('APP_INSTANCE_ID', token)
          const jwe = window.sessionStorage.getItem('jwe')

          if (logged && jwe) {
            // if user is logged
            sendAppId(token, permission) // call BE to set app_instance
          } else {
            setTimeout(() => getNewFCM(permission), 1000 * 60) // long polling ogni minuto
          }
        } else {
          setTimeout(() => getNewFCM(permission), 1000 * 60)
        }
      })
      .catch((error) => {
        console.error('[FirebaseMessaging] getToken error:', error)
        setTimeout(() => getNewFCM(permission), 1000 * 60)
      })
  }

  const checkConnection = async () => {
    // Check Network status
    const status = await Network.getStatus()
    if (!status.connected) {
      onNetworkDisconnected()
    }
  }
}

export default AppInit
